import { ContextStore, Store } from 'bernie-plugin-mobx';
import { observable } from 'mobx';

import { getBexApiContext } from 'bernie-context';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { SerializedData } from 'bernie-core';

import { BexApiSource } from 'sources/bex-api-source';

import { buildSRPVariables } from 'stores/utils';
import { STORE_EVENTS } from './events/store-events';

import { carouselSearchTemplate } from '../templates/search/carousel-search.template';
import { conventionalLodgingTemplate } from 'templates/property-details/conventional-lodging.template';
import { listViewTemplate } from 'templates/search/list-view.template';
import { PropertyInfoPageLayout, SearchResultsPageLayout } from '__generated__/api';
import { singleOfferLargeTemplate } from 'templates/property-details/single-offer-large.template';
import { singleOfferSmallTemplate } from 'templates/property-details/single-offer-small.template';
import { splitViewTemplate } from 'templates/search/split-view.template';

export class PageLayoutStore extends Store {
  public static DEFAULT_PDP_TEMPLATE_NAME: string = 'CONVENTIONAL_LODGING';
  public static DEFAULT_SRP_TEMPLATE_NAME: string = 'LIST_VIEW';

  private static viewComposerTemplates = ['SINGLE_UNIT_VR'];
  public static shouldUseViewComposer = (templateName: string): boolean => PageLayoutStore.viewComposerTemplates.includes(templateName);

  @observable
  public templateName: string;

  constructor(private bexApiSource: BexApiSource, state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    delete data.source;
    delete data.bexApiSource;

    this.templateName = data.templateName;
  }

  public async fetchPDP(propertyId: string, context: ContextStore, isPackagePath): Promise<string> {
    if (isPackagePath) {
      this.templateName = PageLayoutStore.DEFAULT_PDP_TEMPLATE_NAME;
    } else {
      const variables: PropertyInfoPageLayout.Variables = this.buildVariables(context, propertyId);

      const result = await this.bexApiSource.fetchPageLayoutTemplate(variables);

      const templateName = result?.data?.propertyInfo?.experimental?.pageLayoutExperimentalFeature?.templateName;

      this.templateName = templateName || PageLayoutStore.DEFAULT_PDP_TEMPLATE_NAME;
    }

    return this.templateName;
  }

  public async fetchSRP(context: ContextStore, destination: string): Promise<string> {
    const variables: SearchResultsPageLayout.Variables = buildSRPVariables(context, destination);

    const result = await this.bexApiSource.fetchSRPLayoutTemplate?.(variables);

    const templateName = result?.data?.propertySearch?.experimental?.pageLayoutFeatureExperiment?.templateName;

    this.templateName = templateName || PageLayoutStore.DEFAULT_SRP_TEMPLATE_NAME;

    return this.templateName;
  }

  private buildVariables(context: ContextStore, propertyId: string): PropertyInfoPageLayout.Variables {
    return {
      context: getBexApiContext(context),
      shoppingContext: null,
      propertyId,
    };
  }

  public getSRPTemplate = (templateName?: string) => {
    const templateMap = {
      LIST_VIEW: listViewTemplate,
      SPLIT_VIEW: splitViewTemplate,
    };

    const template = templateMap[templateName];
    if (template) {
      return template;
    }
    const message = `No matching templateName="${templateName}". Falling back to default.`;
    this.logger.logEvent(STORE_EVENTS.DEFAULT_SRP_PAGE_LAYOUT, message);

    return listViewTemplate;
  };

  public getCarouselSearchTemplate = () => carouselSearchTemplate;

  public getPDPTemplate = (templateName?: string) => {
    const templateMap = {
      SINGLE_UNIT_VR_LARGE: singleOfferLargeTemplate,
      SINGLE_UNIT_VR_SMALL: singleOfferSmallTemplate,
      CONVENTIONAL_LODGING_LARGE: conventionalLodgingTemplate,
      CONVENTIONAL_LODGING_SMALL: conventionalLodgingTemplate,
    };

    const template = templateMap[templateName];
    if (template) {
      return template;
    }
    const message = `No matching templateName="${templateName}". Falling back to default.`;
    this.logger.logEvent(STORE_EVENTS.DEFAULT_PDP_PAGE_LAYOUT, message);

    return conventionalLodgingTemplate;
  };
}
